<template>
    <div class="flex flex-col gap-3 p-2">
        <h1 class="text-lg font-semibold text-center">{{ t('configScanner.title') }}</h1>

        <BarcodeScanner :on-detected="onDetected" />

        <ul v-if="log.length" class="space-y-1 text-sm">
            <li
                v-for="(line, i) in log"
                :key="i"
                :class="line.error ? 'text-error' : 'text-success'"
            >
                {{ line.text }}
            </li>
        </ul>

        <AppDialog v-model="loginRequiredOpen">
            <div class="p-5 flex flex-col gap-4">
                <h2 class="text-base font-semibold">{{ t('configScanner.loginRequiredTitle') }}</h2>
                <p class="text-sm opacity-90">{{ t('configScanner.loginRequiredMessage') }}</p>
                <AppButton variant="primary" block @click="loginRequiredOpen = false">
                    {{ t('configScanner.ok') }}
                </AppButton>
            </div>
        </AppDialog>
    </div>
</template>

<script setup lang="ts">
    import { ref } from 'vue'
    import { useI18n } from 'vue-i18n'
    import BarcodeScanner from '@/components/BarcodeScanner.vue'
    import AppDialog from '@/components/ui/AppDialog.vue'
    import AppButton from '@/components/ui/AppButton.vue'
    import { useApplyConfig } from '@/composables/useApplyConfig'

    const { t } = useI18n()
    const { apply } = useApplyConfig()

    const log = ref<Array<{ text: string; error: boolean }>>([])
    const busy = ref(false)
    const loginRequiredOpen = ref(false)

    function pushLog(text: string, error = false) {
        log.value.unshift({ text, error })
    }

    async function onDetected(raw: string) {
        if (busy.value) return
        busy.value = true
        const outcome = await apply(raw)

        if (outcome.status === 'notConfig') {
            pushLog(t('configScanner.notConfig'), true)
            busy.value = false
            return
        }
        if (outcome.status === 'needsLogin') {
            loginRequiredOpen.value = true
            busy.value = false
            return
        }

        const result = outcome.result
        if (result) {
            for (const a of result.applied) {
                if (a.type === 'LOGIN') pushLog(`${t('configScanner.loggedIn')} ${a.username}`)
                else if (a.type === 'TICKETS_T') pushLog(`${t('configScanner.setsSelected')} ${a.ids.join(', ')}`)
                else if (a.type === 'TICKETS_Z') pushLog(`${t('configScanner.zonesSelected')} ${a.ids.join(', ')}`)
            }
            for (const e of result.errors) pushLog(e, true)
        }
        if (outcome.status === 'failed') busy.value = false
    }
</script>
